import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

const STORAGE_KEY = "plp_grid_view";
const GRID_VIEW = "grid";
const SINGLE_VIEW = "single";

/**
 * Grid toggle component for PLP mobile/tablet views.
 *
 * @typedef {object} GridToggleRefs
 * @property {HTMLButtonElement[]} [toggleButtons]
 *
 * @extends {Component<GridToggleRefs>}
 */
class GridToggle extends Component {
  requiredRefs = ["toggleButtons"];
  /** @type {MutationObserver | undefined} */
  #gridObserver;
  /** @type {"grid" | "single"} */
  #currentView = GRID_VIEW;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", this.#handleClick);
    this.applyStoredView();
    this.#observeGridUpdates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.#handleClick);
    this.#gridObserver?.disconnect();
  }

  /**
   * @param {Event} event
   */
  #handleClick = event => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest("[data-grid-view]");
    if (!(button instanceof HTMLButtonElement)) return;
    const view = button.dataset.gridView;
    if (view !== GRID_VIEW && view !== SINGLE_VIEW) return;
    this.#setView(view);
  };

  #applyStoredView() {
    const storedView = this.#getStoredView();
    this.#setView(storedView, { persist: false });
  }

  /**
   * @param {"grid" | "single"} view
   * @param {{ persist?: boolean }} [options]
   */
  #setView(view, options = {}) {
    this.#currentView = view;
    this.#updateButtons(view);
    this.#applyViewToGrid(view);
    if (options.persist !== false) {
      this.#storeView(view);
    }
    document.dispatchEvent(
      new CustomEvent("grid-view:change", {
        detail: { view },
        bubbles: true,
      })
    );
  }

  /**
   * @param {"grid" | "single"} view
   */
  #updateButtons(view) {
    const { toggleButtons } = this.refs;
    if (!Array.isArray(toggleButtons)) return;
    for (const button of toggleButtons) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const isActive = button.dataset.gridView === view;
      button.setAttribute("aria-current", isActive ? "true" : "false");
    }
  }

  /**
   * @param {"grid" | "single"} view
   */
  #applyViewToGrid(view) {
    const grid = this.#getGridElement();
    if (!(grid instanceof HTMLElement)) return;
    grid.dataset.gridView = view;
    grid.classList.toggle("product-list-grid--single", view === SINGLE_VIEW);
    const loader = this.#getLoaderElement();
    if (loader instanceof HTMLElement) {
      loader.dataset.gridView = view;
    }
  }

  #getGridElement() {
    const resultsList = this.closest("results-list") || document.querySelector("results-list");
    return resultsList?.querySelector('[ref="grid"]') || null;
  }

  #observeGridUpdates() {
    const resultsList = this.closest("results-list") || document.querySelector("results-list");
    if (!resultsList) return;
    this.#gridObserver = new MutationObserver(() => {
      this.#applyViewToGrid(this.#currentView);
    });
    this.#gridObserver.observe(resultsList, {
      childList: true,
      subtree: true,
    });
  }

  applyStoredView() {
    this.#applyStoredView();
  }

  #getLoaderElement() {
    return document.querySelector("[data-loader]");
  }

  #getStoredView() {
    try {
      const stored = window.localStorage?.getItem(STORAGE_KEY);
      if (stored === GRID_VIEW || stored === SINGLE_VIEW) return stored;
    } catch (error) {
      // Ignore storage errors and fall back to default.
    }
    return GRID_VIEW;
  }

  /**
   * @param {"grid" | "single"} view
   */
  #storeView(view) {
    try {
      window.localStorage?.setItem(STORAGE_KEY, view);
    } catch (error) {
      // Ignore storage errors.
    }
  }
}

if (!customElements.get("grid-toggle")) {
  customElements.define("grid-toggle", GridToggle);
}
